import React, { useState, useEffect, useCallback, useRef } from "react";
import { Theme } from "@mui/material";
import { createStyles, makeStyles } from "@mui/styles";
import { PartnersFooter } from "../../components/PartnersFooter";
import { PaymentDetail } from "../../components/common/PaymentDetail";
import { PurchaseRequest, RootState, Session } from "../../store/config/types";
import { connect, ConnectedProps } from "react-redux";
import { ThunkDispatch } from "redux-thunk";
import { AppActions } from "../../store/config/ActionTypes";
import { getPlexoUrl, getActiveIssuers } from "../../store/action_creators/instruments.actions";
import { useHistory } from "react-router-dom";
import { urlVarsHelper } from "../../helpers/urlVarsHelper";
import { fetchPurchaseData } from "../../store/action_creators/purchases.actions";
import { v4 as uuid } from "uuid";
import { PaymentStatusEnum } from "../../store/config/enums";

const PLEXO_EVENT_VERSION = 1;
const PLEXO_LOG_PREFIX = "[PlexoEmbeddedCheckout]";

const logCheckoutWarning = (reason: string, details: Record<string, unknown>) => {
  console.warn(`${PLEXO_LOG_PREFIX} ${reason}`, details);
};

const logCheckoutError = (reason: string, details: Record<string, unknown>) => {
  console.error(`${PLEXO_LOG_PREFIX} ${reason}`, details);
};

const getOriginFromUrl = (url: string): string | null => {
  try {
    return new URL(url).origin;
  } catch {
    return null;
  }
};

export const navigateToTopLevel = (url: string): void => {
  if (window.top) {
    window.top.location.assign(url);
  } else {
    window.location.assign(url);
  }
};

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    mainContainer: {
      width: "45%",
      margin: "auto",
      flex: 1,
      display: "flex",
      alignItems: "center",
    },
    dataContainer: {
      height: "429px",
      display: "flex",
    },
    instructionsContainer: {
      width: "46%",
    },
    paymentContainer: {
      width: "54%",
    },
    button: {
      fontSize: 16,
    },
    iFrame: {
      border: 0,
    },
  })
);

const mapStateToProps = (state: RootState) => ({
  purchases: state.purchases,
  instruments: state.instruments,
});

const mapDispatchToProps = (dispatch: ThunkDispatch<RootState, any, AppActions>) => ({
  getPlexoUrl: ({ sessionId, fingerprint }: PurchaseRequest) => dispatch(getPlexoUrl({ sessionId, fingerprint })),
  fetchPurchaseData: ({ sessionId }: Session) => dispatch(fetchPurchaseData({ sessionId })),
  getIssuers: ({ sessionId }: Session) => dispatch(getActiveIssuers({ sessionId })),
});

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

function DetailsScreen({
  purchases,
  instruments,
  getPlexoUrl,
  fetchPurchaseData,
  getIssuers
}: PropsFromRedux) {
  const classes = useStyles();
  const history = useHistory();
  const urlVars = urlVarsHelper.getUrlVars();
  const [showConfirmContainer, setShowConfirmContainer] = useState(true);
  const [checkoutOrigin, setCheckoutOrigin] = useState<string | null>(null);
  const checkoutIframeRef = useRef<HTMLIFrameElement | null>(null);
  const checkoutInitSentRef = useRef(false);

  //#region fingerprint

  const isTest =
    window.location.host === "localhost:3000" ||
    window.location.host === "handytest.netlify.app" ||
    window.location.host === "handytestwebsite.handsoft.com.uy" ||
    window.location.host === "pago.arriba.uy";
  const environmentId = isTest ? "1snn5n9w" : "k8vif92e";
  const commerceId = isTest ? "123" : "visanetuy_px_2048256716";
  const fingerprintId = uuid();
  const visaFingerprint = commerceId + fingerprintId;

  const loadVisaDeviceFingerprint = (callback: any) => {
    const existingScript = document.getElementById("visaDeviceFingerprint");
    if (!existingScript) {
      const script = document.createElement("script");
      script.src = `https://h.online-metrix.net/fp/tags.js?org_id=${environmentId}&session_id=${visaFingerprint}`;
      script.id = "visaDeviceFingerprint";
      document.body.appendChild(script);

      script.onload = () => {
        if (callback) callback();
      };
    }

    if (existingScript && callback) callback();
  };

  useEffect(() => {
    loadVisaDeviceFingerprint(() => {
      return;
    });
  });

  //#endregion

  const redirectTo = useCallback(
    (path: string): void => {
      history.push(path);
    },
    [history]
  );

  const handleCheckoutIframeLoad = useCallback(() => {
    if (!instruments.url || checkoutInitSentRef.current) {
      return;
    }

    const iframeWindow = checkoutIframeRef.current?.contentWindow ?? null;

    if (!checkoutOrigin || !iframeWindow) {
      logCheckoutWarning("checkout-init-skipped", {
        reason: !checkoutOrigin ? "missing-checkout-origin" : "missing-iframe-window",
        hasCheckoutUrl: Boolean(instruments.url),
        hasCheckoutOrigin: Boolean(checkoutOrigin),
        hasIframeWindow: Boolean(iframeWindow),
      });
      return;
    }

    try {
      iframeWindow.postMessage(
        {
          plexo: {
            version: PLEXO_EVENT_VERSION,
            event: "checkout-init",
          },
        },
        checkoutOrigin
      );

      checkoutInitSentRef.current = true;
    } catch {
      logCheckoutError("checkout-init-send-failed", {
        expectedOrigin: checkoutOrigin,
        hasCheckoutUrl: true,
      });
    }
  }, [checkoutOrigin, instruments.url]);

  useEffect(() => {
    checkoutInitSentRef.current = false;

    if (!instruments.url) {
      setCheckoutOrigin(null);
      return;
    }

    const origin = getOriginFromUrl(instruments.url);

    if (!origin) {
      setCheckoutOrigin(null);
      logCheckoutError("checkout-url-invalid", {
        hasCheckoutUrl: true,
      });
      return;
    }

    setCheckoutOrigin(origin);
  }, [instruments.url]);

  useEffect(() => {
    const receiveMessage = (event: MessageEvent) => {
      const iframeWindow = checkoutIframeRef.current?.contentWindow ?? null;
      const sourceMatches = Boolean(iframeWindow && event.source === iframeWindow);

      if (event.data === "redirectError") {
        if (!sourceMatches) {
          logCheckoutWarning("ignored-legacy-redirect", {
            reason: "source-mismatch",
            legacyEvent: "redirectError",
            sourceMatches,
          });
          return;
        }

        redirectTo("/error/?errorCode=4");
        return;
      }

      if (event.data === "redirectSuccess") {
        if (!sourceMatches) {
          logCheckoutWarning("ignored-legacy-redirect", {
            reason: "source-mismatch",
            legacyEvent: "redirectSuccess",
            sourceMatches,
          });
          return;
        }

        if (
          purchases.transactionCallbackResponse &&
          purchases.transactionCallbackResponse.status === PaymentStatusEnum.PENDING
        ) {
          redirectTo("/result");
        }
        return;
      }

      const message =
        (event.data as {
          plexo?: {
            version?: number;
            event?: string;
            redirectUrl?: string;
            [key: string]: unknown;
          };
        })?.plexo;
      if (!message) {
        return;
      }

      const messageEvent = typeof message.event === "string" ? message.event : "unknown";

      if (!checkoutOrigin) {
        logCheckoutWarning("ignored-plexo-message", {
          reason: "missing-checkout-origin",
          eventName: messageEvent,
          receivedOrigin: event.origin,
          sourceMatches,
        });
        return;
      }

      if (event.origin !== checkoutOrigin || !sourceMatches) {
        logCheckoutWarning("ignored-plexo-message", {
          reason: event.origin !== checkoutOrigin ? "origin-mismatch" : "source-mismatch",
          eventName: messageEvent,
          expectedOrigin: checkoutOrigin,
          receivedOrigin: event.origin,
          sourceMatches,
        });
        return;
      }

      if (message.version !== PLEXO_EVENT_VERSION) {
        logCheckoutWarning("ignored-plexo-message", {
          reason: "unsupported-version",
          eventName: messageEvent,
          expectedVersion: PLEXO_EVENT_VERSION,
          receivedVersion: typeof message.version === "number" ? message.version : "invalid",
        });
        return;
      }

      switch (messageEvent) {
        case "checkout-redirect": {
          const redirectUrl = typeof message.redirectUrl === "string" ? message.redirectUrl.trim() : "";
          if (!redirectUrl) {
            logCheckoutWarning("ignored-checkout-redirect", {
              reason: "missing-redirect-url",
              eventName: messageEvent,
              hasRedirectUrl: false,
            });
            return;
          }

          let parsedRedirectUrl: URL;
          try {
            parsedRedirectUrl = new URL(redirectUrl);
          } catch {
            logCheckoutWarning("ignored-checkout-redirect", {
              reason: "invalid-redirect-url",
              eventName: messageEvent,
              hasRedirectUrl: true,
            });
            return;
          }

          if (parsedRedirectUrl.protocol !== "https:" && parsedRedirectUrl.protocol !== "http:") {
            logCheckoutWarning("ignored-checkout-redirect", {
              reason: "unsupported-redirect-protocol",
              eventName: messageEvent,
              redirectProtocol: parsedRedirectUrl.protocol,
            });
            return;
          }

          try {
            navigateToTopLevel(parsedRedirectUrl.href);
          } catch {
            try {
              window.location.assign(parsedRedirectUrl.href);
            } catch {
              logCheckoutError("checkout-redirect-navigation-failed", {
                eventName: messageEvent,
                expectedOrigin: checkoutOrigin,
                receivedOrigin: event.origin,
                redirectProtocol: parsedRedirectUrl.protocol,
              });
            }
          }

          return;
        }

        case "checkout-ready":
        case "checkout-pending":
        case "checkout-completed":
        case "checkout-cancelled":
        case "height":
          // Si algún día queremos hacer que el tamaño del iframe se ajuste automáticamente
          return;

        case "checkout-failed":
          logCheckoutError("checkout-failed", {
            eventName: messageEvent,
            payload: message,
          });
          return;

        default:
          logCheckoutWarning("ignored-plexo-message", {
            reason: "unknown-event",
            eventName: messageEvent,
            expectedOrigin: checkoutOrigin,
            receivedOrigin: event.origin,
          });
          return;
      }
    };

    window.addEventListener("message", receiveMessage);

    return () => {
      window.removeEventListener("message", receiveMessage);
    };
  }, [checkoutOrigin, purchases.transactionCallbackResponse, redirectTo]);

  useEffect(() => {
    if (purchases.purchaseSuccess && purchases.transactionCallbackResponse?.status !== PaymentStatusEnum.PENDING) {
      history.push(`/result?isCard=${!instruments.instrument?.notACard}`);
    }

    if (purchases.purchaseError !== null) {
      redirectTo(instruments.instrument?.notACard ? "/error?errorCode=8" : "/error?errorCode=3");
    }
  }, [purchases.purchaseSuccess, purchases.purchaseError, purchases.transactionCallbackResponse, redirectTo, instruments,history]);

  useEffect(() => {
    const continueWithPayment = () => {
      if (purchases.sessionId) {
        getPlexoUrl({
          sessionId: purchases.sessionId,
          fingerprint: isTest ? "d1cab7e203194d09972b7fb3d4657fc8" : fingerprintId,
        });
        setShowConfirmContainer(false);
        window.scrollTo(0, 0);
      }
    };

    if (urlVars.sessionId) {
      if (!purchases.sessionId) {
        fetchPurchaseData({ sessionId: urlVars.sessionId });
      }
      if (
        !instruments.loadingActiveIssuers &&
        instruments.activeIssuers === null &&
        instruments.activeIssuersError === null &&
        purchases.loadPurchaseDataSuccess &&
        purchases.sessionId
      ) {
        getIssuers({ sessionId: purchases.sessionId });
        continueWithPayment();
      }
    } else if (!purchases.sessionId) {
      history.push("/error?errorCode=6");
    }

    if (urlVars.from) {
      if (urlVars.from === "confirm") {
        if (purchases.sessionId) {
          getPlexoUrl({
            sessionId: purchases.sessionId,
            fingerprint: isTest ? "d1cab7e203194d09972b7fb3d4657fc8" : fingerprintId,
          });
        }
        setShowConfirmContainer(false);
        window.scrollTo(0, 0);
      }
    }

    if (purchases.loadPurchaseDataError) {
      history.push("/error?errorCode=99");
    }
  }, [
    purchases.sessionId,
    fetchPurchaseData,
    redirectTo,
    getPlexoUrl,
    purchases.loadPurchaseDataError,
    purchases.loadPurchaseDataSuccess,
    getIssuers,
    instruments.activeIssuers,
    instruments.activeIssuersError,
    instruments.loadingActiveIssuers,
    urlVars.from,
    urlVars.sessionId,
    fingerprintId,
    isTest,
    history
  ]);

  useEffect(() => {
    if (instruments.urlError != null) {
      redirectTo("/error/?errorCode=5");
    }
  }, [instruments.urlError, redirectTo]);

  useEffect(() => {
    sessionStorage.setItem('lastPurchaseData', JSON.stringify(purchases?.purchaseData));
  }, [purchases?.purchaseData]);

  return (
    <div className="details-screen payment-details-screen">
      <div className="data-container">
        {showConfirmContainer ? (
          <div className="confirm-cat-container">

            {instruments.activeIssuers !== null && <PartnersFooter issuers={instruments.activeIssuers} />}
          </div>
        ) : (
          <div className="iframe-container">
            <iframe
              ref={checkoutIframeRef}
              width={500}
              src={instruments.url ? instruments.url : "/?plexoLoading=true"}
              title="plexoIframe"
              allow="payment"
              onLoad={handleCheckoutIframeLoad}
              className={classes.iFrame}
            ></iframe>
          </div>
        )}
        <PaymentDetail data={purchases.purchaseData} />
        {showConfirmContainer && (
          <div className="mobile-pay-action-box">
            {instruments.activeIssuers !== null && <PartnersFooter issuers={instruments.activeIssuers} />}
          </div>
        )}
      </div>
    </div>
  );
}

//TODO: Achicar componente

export default connector(DetailsScreen);
